/**
 * Everyone a map can be about: this therapist's patients, couples and groups.
 *
 * Needed only on the home surface, where no subject is in context. A patient's
 * dock already knows whose family it is drawing and asks nobody.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCourseLabels, useSessionPreferences } from "../../center";

import { useTherapist } from "../../../context/useTherapist";
import { CouplePatientAPI, useGroupPatients, groupLabel, listAllTherapistPatientsResponse } from "../../patient";
import type { PatientOption } from "../../../shared/ui/patient/PatientFinder";

export const MAP_SUBJECT_PATIENTS_KEY = "systemic-map-patients";
export const MAP_SUBJECT_COUPLES_KEY = "systemic-map-couples";

export function useSystemicMapSubjects(enabled = true) {
    const { therapist } = useTherapist();
    // A training centre's student, with the course they are on under the name.
    const { courseOf } = useCourseLabels();
    // Couples and groups only where the centre runs them.
    const { modes } = useSessionPreferences();

    const patientsQuery = useQuery({
        queryKey: [MAP_SUBJECT_PATIENTS_KEY, therapist?.id],
        queryFn: async () => {
            const { data } = await listAllTherapistPatientsResponse(therapist?.id as string);
            return data.patients;
        },
        enabled: enabled && Boolean(therapist?.id),
    });

    const couplesQuery = useQuery({
        queryKey: [MAP_SUBJECT_COUPLES_KEY, therapist?.id],
        queryFn: async () => {
            const { data } = await CouplePatientAPI.listByTherapist(
                therapist?.id as string,
            );
            return data;
        },
        enabled: enabled && Boolean(therapist?.id),
    });

    const groupsQuery = useGroupPatients(enabled && Boolean(therapist?.id));

    // Individuals first, then couples, then groups — the same order the rest
    // of the product offers a caseload in.
    const options = useMemo<PatientOption[]>(
        () => [
            ...(patientsQuery.data ?? []).map((patient) => ({
                id: patient.id,
                name: `${patient.first_name} ${patient.last_name}`,
                kind: "patient" as const,
                detail: courseOf(patient.course_id),
            })),
            ...(modes.couple ? couplesQuery.data ?? [] : []).map((couple) => ({
                id: couple.id,
                name: couple.display_name,
                kind: "couple" as const,
            })),
            ...(modes.group ? groupsQuery.groups : []).map((group) => ({
                id: group.id,
                name: groupLabel(group),
                kind: "group" as const,
            })),
        ],
        [patientsQuery.data, couplesQuery.data, groupsQuery.groups, courseOf, modes.couple, modes.group],
    );

    return {
        options,
        isLoading: patientsQuery.isLoading || couplesQuery.isLoading || groupsQuery.loading,
        isError: patientsQuery.isError || couplesQuery.isError || groupsQuery.failed,
    };
}
